import type { ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { Label } from "@/components/ui/input";
import { cn } from "@/lib/utils";

/** Label + control + hint/error, with the error translated from its i18n key. */
export function Field({
  label,
  htmlFor,
  hint,
  error,
  optional,
  className,
  children,
}: {
  label: ReactNode;
  htmlFor?: string;
  hint?: ReactNode;
  error?: string | undefined;
  optional?: boolean;
  className?: string;
  children: ReactNode;
}) {
  const { t } = useTranslation();
  const message = error
    ? error.startsWith("validation.")
      ? t(error as "validation.required")
      : error
    : null;
  return (
    <div className={cn("flex flex-col gap-1.5", className)}>
      <Label htmlFor={htmlFor} className="flex items-baseline gap-1.5">
        {label}
        {optional ? (
          <span className="text-xs font-normal text-muted-foreground">
            ({t("common.optional")})
          </span>
        ) : null}
      </Label>
      {children}
      {message ? (
        <p className="text-xs text-destructive" role="alert">
          {message}
        </p>
      ) : hint ? (
        <p className="text-xs text-muted-foreground">{hint}</p>
      ) : null}
    </div>
  );
}

export function FormSection({
  title,
  children,
  className,
}: {
  title?: ReactNode;
  children: ReactNode;
  className?: string;
}) {
  return (
    <fieldset className={cn("flex flex-col gap-4", className)}>
      {title ? (
        <legend className="mb-3 text-xs font-semibold tracking-wide text-muted-foreground uppercase">
          {title}
        </legend>
      ) : null}
      {children}
    </fieldset>
  );
}
